import { get, writable } from 'svelte/store';

export type HeaderChromePackage =
	| 'current'
	| 'compact'
	| 'glass'
	| 'glow'
	| 'chip'
	| 'segment'
	| 'dense';

export type HeaderChromeStyle = 'current' | 'compact' | 'glass';
export type HeaderTabsStyle = 'underline' | 'minimal' | 'pill' | 'chip' | 'segment' | 'glow';

export type HeaderChromePackageConfig = {
	id: HeaderChromePackage;
	label: string;
	blurb: string;
	chromeStyle: HeaderChromeStyle;
	tabsStyle: HeaderTabsStyle;
};

export const HEADER_CHROME_PACKAGES: HeaderChromePackageConfig[] = [
	{
		id: 'current',
		label: 'Current',
		blurb: 'Baseline underline tabs',
		chromeStyle: 'current',
		tabsStyle: 'underline',
	},
	{
		id: 'compact',
		label: 'Compact',
		blurb: 'Tighter chrome + quiet rail',
		chromeStyle: 'compact',
		tabsStyle: 'minimal',
	},
	{
		id: 'glass',
		label: 'Glass',
		blurb: 'Dock-like sliding pill',
		chromeStyle: 'glass',
		tabsStyle: 'pill',
	},
	{
		id: 'glow',
		label: 'Glow',
		blurb: 'Sliding plate with primary edge glow',
		chromeStyle: 'glass',
		tabsStyle: 'glow',
	},
	{
		id: 'chip',
		label: 'Chip',
		blurb: 'Smaller rounded sliding chips',
		chromeStyle: 'compact',
		tabsStyle: 'chip',
	},
	{
		id: 'segment',
		label: 'Segment',
		blurb: 'Segmented control tray',
		chromeStyle: 'glass',
		tabsStyle: 'segment',
	},
	{
		id: 'dense',
		label: 'Dense',
		blurb: 'Compact chrome + sliding pill',
		chromeStyle: 'compact',
		tabsStyle: 'pill',
	},
];

const PACKAGE_KEY = 'shelly_dev_header_chrome_package';
const FADE_KEY = 'shelly_dev_header_content_fade_height';

export const HEADER_CONTENT_FADE_HEIGHT_DEFAULT = 32;
export const HEADER_CONTENT_FADE_HEIGHT_MIN = 0;
export const HEADER_CONTENT_FADE_HEIGHT_MAX = 160;

const PACKAGE_IDS = new Set(HEADER_CHROME_PACKAGES.map((p) => p.id));

const readPackage = (): HeaderChromePackage => {
	try {
		const stored = localStorage.getItem(PACKAGE_KEY);
		if (stored && PACKAGE_IDS.has(stored as HeaderChromePackage)) {
			return stored as HeaderChromePackage;
		}
	} catch {
		// localStorage may be unavailable
	}
	return 'glass';
};

const clampFadeHeight = (value: number): number => {
	if (!Number.isFinite(value)) return HEADER_CONTENT_FADE_HEIGHT_DEFAULT;
	return Math.min(
		HEADER_CONTENT_FADE_HEIGHT_MAX,
		Math.max(HEADER_CONTENT_FADE_HEIGHT_MIN, Math.round(value)),
	);
};

const readFadeHeight = (): number => {
	try {
		const stored = localStorage.getItem(FADE_KEY);
		if (stored === null) return HEADER_CONTENT_FADE_HEIGHT_DEFAULT;
		const parsed = Number(stored);
		// Migrate prior extremes (too roomy / too tight) to the under-tabs default.
		if (parsed === 16 || parsed === 40 || parsed === 64) return HEADER_CONTENT_FADE_HEIGHT_DEFAULT;
		return clampFadeHeight(parsed);
	} catch {
		return HEADER_CONTENT_FADE_HEIGHT_DEFAULT;
	}
};

const applyFadeHeightCss = (px: number) => {
	if (typeof document === 'undefined') return;
	document.documentElement.style.setProperty('--header-content-fade-height', `${px}px`);
};

/** Active header chrome package (Header + Tabs paired). Default glass. */
export const headerChromePackage = writable<HeaderChromePackage>(readPackage());

headerChromePackage.subscribe((value) => {
	try {
		localStorage.setItem(PACKAGE_KEY, value);
	} catch {
		// localStorage may be unavailable
	}
});

export const getHeaderChromePackage = (): HeaderChromePackage => get(headerChromePackage);

export const setHeaderChromePackage = (id: HeaderChromePackage) => {
	if (!PACKAGE_IDS.has(id)) return;
	headerChromePackage.set(id);
};

export const getHeaderChromePackageConfig = (
	id: HeaderChromePackage = get(headerChromePackage),
): HeaderChromePackageConfig =>
	HEADER_CHROME_PACKAGES.find((p) => p.id === id) ?? HEADER_CHROME_PACKAGES[2];

/** Top content dissolve under sticky tabs (px). Applied as `--header-content-fade-height`. */
export const headerContentFadeHeightPx = writable<number>(readFadeHeight());

headerContentFadeHeightPx.subscribe((value) => {
	const next = clampFadeHeight(value);
	try {
		localStorage.setItem(FADE_KEY, String(next));
	} catch {
		// localStorage may be unavailable
	}
	applyFadeHeightCss(next);
});

export const getHeaderContentFadeHeightPx = (): number => get(headerContentFadeHeightPx);

export const setHeaderContentFadeHeightPx = (px: number) => {
	headerContentFadeHeightPx.set(clampFadeHeight(px));
};
